@if (isLoading) {
<div class="loader-overlay" role="status" aria-live="polite">
  <div class="heart-wrapper" aria-hidden="true">
    <svg class="heart-svg" viewBox="0 0 24 24">
      <path class="heart-shape" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4 8.24 4 9.91 5 10.54 6.36
             11.17 5 12.76 4 14.5 4 17 4 19 6 19 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
    </svg>
  </div>
  <p class="loader-text mt-3 fw-semibold text-dark">Loading...</p>
</div>
}

@if (!!booking && !isLoading) {
<div class="booking-view-container">
  <div class="booking-hero card border-0">
    <div class="booking-hero__content">
      <div>
        <div class="booking-hero__eyebrow">Appointment overview</div>
        <h2 class="booking-hero__title mb-2">
          {{ booking.patient.firstName }} {{ booking.patient.lastName }}
        </h2>
        <div class="booking-hero__meta">
          <span><i class="bi bi-person-badge me-1"></i>{{
            (booking.doctor.user.title ? booking.doctor.user.title + ' ' : '') +
            booking.doctor.user.lastName
            }}</span>
          <span><i class="bi bi-geo-alt me-1"></i>{{ booking.bookingSlot.slotDate | date: 'dd MMM yyyy' }} at
            {{ booking.bookingSlot.slotTime }}</span>
          <span><i class="bi bi-hash me-1"></i>{{ booking.reference }}</span>
        </div>
      </div>

      <div class="booking-hero__status">
        <span class="status-pill" [ngClass]="getStatusClass(booking.status)">{{
          booking.status
          }}</span>
        <div class="booking-hero__status-note">Reason: {{ booking.reasonForVisit }}</div>
      </div>
    </div>
  </div>

  <div class="row g-3 booking-grid">
    <div class="col-12 col-xl-8 d-flex flex-column gap-3">
      <div class="card booking-card">
        <div class="card-header booking-card__header">
          <div>
            <div class="booking-card__eyebrow">Booking details</div>
            <h3 class="sub-title mb-0">Patient visit</h3>
          </div>

          <div class="booking-card__actions">
            @if (
            booking.status === AppointmentStatus.Pending ||
            booking.status === AppointmentStatus.Confirmed
            ) {
            <button class="btn btn-outline-danger btn-sm" (click)="cancelBooking()" title="Cancel booking">
              <i class="bi bi-x-circle me-1"></i>Cancel
            </button>
            }

            @if (booking.patientDocumentGeneratable) {
            <button class="btn btn-outline-primary btn-sm" (click)="writeSickNote()" title="Write sick note">
              <i class="bi bi-receipt me-1"></i>Sick note
            </button>

            <button class="btn btn-primary btn-sm" (click)="openConsultationPricingModal()"
              title="Consultation pricing">
              <i class="bi bi-tags-fill me-1"></i>Invoice builder
            </button>
            }
          </div>
        </div>

        <div class="detail-matrix">
          <div class="detail-chip">
            <span class="detail-chip__label">Date</span>
            <span class="detail-chip__value">{{
              booking.bookingSlot.slotDate | date: 'dd MMM yyyy'
              }}</span>
          </div>

          <div class="detail-chip">
            <span class="detail-chip__label">Time</span>
            <span class="detail-chip__value">{{ booking.bookingSlot.slotTime }}</span>
          </div>

          <div class="detail-chip">
            <span class="detail-chip__label">Reason</span>
            <span class="detail-chip__value">{{ booking.reasonForVisit }}</span>
          </div>
        </div>

        <div class="notes-grid">
          <div class="notes-card notes-card--soft">
            <span class="notes-card__label">Patient's Notes</span>
            <p class="notes-card__body mb-0">
              {{ booking.notes || 'No patient notes provided.' }}
            </p>
          </div>

          @if (!isReceptionUser) {
          <div class="notes-card">
            <label for="notes" class="notes-card__label mb-2">Doctor's Notes</label>
            <textarea id="notes" class="form-control" rows="5" [(ngModel)]="booking.doctorNotes"></textarea>

            <div class="mt-3">
              <label for="icd10Code" class="notes-card__label mb-1">
                ICD-10 Code
                <span class="text-muted fw-normal ms-1" style="font-size: 0.75rem">(optional — available during active
                  consultation)</span>
              </label>
              <input id="icd10Code" type="text" class="form-control form-control-sm" [(ngModel)]="booking.icd10Code"
                [disabled]="!booking.inProgress" [class.opacity-50]="!booking.inProgress" placeholder="{{
                      booking.inProgress
                        ? 'e.g. J06.9'
                        : 'Only editable while consultation is in progress'
                    }}" maxlength="20" />
            </div>

            <div class="d-flex justify-content-end mt-2">
              <button class="btn btn-primary btn-sm" type="button" (click)="saveDoctorNotes()"
                [disabled]="isSavingDoctorNotes">
                <i class="bi bi-save me-1"></i>
                {{ isSavingDoctorNotes ? 'Saving...' : 'Save doctor notes' }}
              </button>
            </div>
          </div>
          }
        </div>
      </div>

      <div class="card booking-card">
        <div class="card-header booking-card__header">
          <div>
            <div class="booking-card__eyebrow">Invoice details</div>
            <h3 class="sub-title mb-0">Consultation invoice</h3>
          </div>
          <div class="invoice-total-badge">Total R{{ formatCurrency(invoiceTotal) }}</div>
        </div>

        <div class="card-body invoice-body">
          <div class="invoice-settlement-panel">
            <div class="invoice-settlement-panel__header">
              <div>
                <div class="booking-card__eyebrow mb-1">Invoice settlement</div>
                <h5 class="mb-0 fw-semibold">Partial payments</h5>
              </div>

              <div class="invoice-settlement-summary">
                <span>Total settled: R{{ formatCurrency(totalAmountSettled) }}</span>
                <span>Remaining: R{{ formatCurrency(remainingInvoiceAmount) }}</span>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table invoice-settlement-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Amount Settled</th>
                    <th>Paid By</th>
                    <th class="text-end">Remaining Amount</th>
                    <th class="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of paymentRows; track $index; let i = $index) {
                  <tr>
                    <td class="settlement-index">{{ i + 1 }}</td>
                    <td class="text-nowrap text-muted">
                      {{ row.createdAt ? (row.createdAt | date: 'dd MMM yyyy, HH:mm') : '—' }}
                    </td>
                    <td>
                      <div class="input-group input-group-sm settlement-input-group">
                        <span class="input-group-text">R</span>
                        <input type="number" class="form-control" min="0" step="0.01" [(ngModel)]="row.amountSettled"
                          placeholder="0.00" />
                      </div>
                    </td>
                    <td>
                      <select class="form-select form-select-sm" [(ngModel)]="row.paidBy">
                        <option value="MEDICAL_AID">Medical aid</option>
                        <option value="OUT_OF_POCKET">Out of pocket</option>
                      </select>
                    </td>
                    <td class="text-end fw-semibold">
                      R{{ formatCurrency(getRowRemainingAmount(i, row)) }}
                    </td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-outline-danger" type="button" [disabled]="paymentRows.length === 1"
                        (click)="removePaymentRow(i)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                  }
                </tbody>
              </table>
            </div>

            <div class="invoice-settlement-panel__footer">
              <small class="text-muted">
                Add another payment when the invoice is not fully settled yet.
              </small>

              <div class="d-flex gap-2 flex-wrap justify-content-end">
                @if (canAddPaymentRow) {
                <button class="btn btn-outline-primary btn-sm" type="button" (click)="addPaymentRow()">
                  <i class="bi bi-plus-circle me-1"></i>Add new payment
                </button>
                }

                <button class="btn btn-primary btn-sm" type="button" (click)="savePaidAmount()">
                  <i class="bi bi-save me-1"></i>Save settled total
                </button>
              </div>
            </div>
          </div>

          @if (invoiceItems.length > 0) {
          <div class="table-responsive">
            <table class="table invoice-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="text-center">Qty</th>
                  <th class="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                @for (item of invoiceItems; track item.id) {
                <tr>
                  <td>
                    <div class="fw-semibold">{{ item.serviceItem }}</div>
                  </td>
                  <td class="text-center">1</td>
                  <td class="text-end">R{{ formatCurrency(item.cost) }}</td>
                </tr>
                }
              </tbody>
              <tfoot>
                <tr>
                  <th colspan="2" class="text-end">Grand total</th>
                  <th class="text-end">R{{ formatCurrency(invoiceTotal) }}</th>
                </tr>
              </tfoot>
            </table>
          </div>
          } @else {
          <div class="empty-state empty-state--compact">
            <i class="bi bi-receipt-cutoff fs-3 d-block mb-2"></i>
            No invoice items are available yet.
          </div>
          }
        </div>
      </div>

      <div class="card booking-card">
        <div class="card-header booking-card__header">
          <div>
            <div class="booking-card__eyebrow">Documents</div>
            <h3 class="sub-title mb-0">Attachments</h3>
          </div>
        </div>

        <div class="attachment-toolbar">
          <div class="attachment-field">
            <label class="form-label fw-semibold mb-1">Type</label>
            <select class="form-select" [(ngModel)]="selectedDocumentType">
              <option value="ID">ID Document</option>
              <option value="LAB_RESULTS">Lab Results</option>
              <option value="XRAY">X-Ray</option>
              <option value="PRESCRIPTION">Prescription</option>
              <option value="REFERRAL">Referral</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <!-- File Input -->
          <div class="flex-grow-1">
            <label class="form-label fw-semibold mb-1">File</label>
            <input type="file" class="form-control" accept="image/*,application/pdf" multiple
              (change)="onFilesSelected($event)" />
          </div>

          <div class="attachment-field attachment-field--button">
            <button class="btn btn-primary w-100" (click)="uploadAttachment()"
              [disabled]="!selectedFile || !selectedDocumentType">
              <i class="bi bi-upload me-1"></i>Upload
            </button>
          </div>
        </div>

        <div class="attachment-list">
          @if (!!attachments && attachments.length > 0) {
          <div class="list-group list-group-flush">
            @for (file of attachments; track file.id) {
            <div class="list-group-item attachment-row">
              <div class="attachment-row__left">
                <i class="bi bi-file-earmark-text fs-5"></i>
                <div class="attachment-row__content">
                  <div class="attachment-row__name">{{ file.fileName }}</div>
                  <div class="attachment-row__badges">
                    @if (file.documentType === 'OTHER') {
                    <span class="badge bg-secondary">{{ file.documentType }}</span>
                    }
                    @if (file.documentType === 'LAB_RESULTS') {
                    <span class="badge bg-success">{{ file.documentType }}</span>
                    }
                    @if (file.documentType === 'XRAY') {
                    <span class="badge bg-info text-dark">{{ file.documentType }}</span>
                    }
                    @if (file.documentType === 'ID') {
                    <span class="badge bg-dark">{{ file.documentType }}</span>
                    }
                    @if (file.documentType === 'REFERRAL') {
                    <span class="badge bg-warning text-dark">{{ file.documentType }}</span>
                    }
                    @if (file.documentType === 'PRESCRIPTION') {
                    <span class="badge bg-primary">{{ file.documentType }}</span>
                    }
                  </div>
                </div>
              </div>

              <div class="attachment-row__actions">
                <button class="btn btn-sm btn-outline-primary" (click)="downloadFile(file)">
                  <i class="bi bi-download"></i>
                </button>
                @if (file.enableDelete) {
                <button class="btn btn-sm btn-outline-danger" (click)="deleteFile(file.id)">
                  <i class="bi bi-trash"></i>
                </button>
                }
              </div>
            </div>
            }
          </div>
          } @else {
          <div class="empty-state empty-state--compact">
            <i class="bi bi-paperclip fs-3 d-block mb-2"></i>
            No attachments uploaded
          </div>
          }
        </div>
      </div>

      <app-patient-health-history [healthHistory]="healthHistory"></app-patient-health-history>
    </div>

    <div class="col-12 col-xl-4">
      <div class="card booking-card booking-card--sticky">
        <div class="card-header booking-card__header">
          <div>
            <div class="booking-card__eyebrow">Patient file</div>
            <h3 class="sub-title mb-0">Summary</h3>
          </div>
        </div>

        <div class="card-body patient-summary-body">
          <app-patient-file-summary [booking]="booking"
            [emergencyContact]="emergencyContact"></app-patient-file-summary>
        </div>
      </div>
    </div>
  </div>

  <div class="row mt-2">
    <div class="col-12 col-xl-8 ms-auto">
      @if (medications != null && medications.length > 0) {
      <div class="card booking-card prescription-card">
        <div class="card-header booking-card__header">
          <div>
            <div class="booking-card__eyebrow">Medication</div>
            <h3 class="sub-title mb-0">Prescription summary</h3>
          </div>

          @if (booking.showPrescriptionDocumentOption) {
          <button class="btn btn-outline-primary btn-sm" (click)="downPrescription()">
            <i class="bi bi-download me-1"></i>Download
          </button>
          }
        </div>

        <div class="table-responsive">
          <table class="table table-sm prescription-table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Dosage</th>
                <th>Frequency</th>
                <th>Applied Area</th>
              </tr>
            </thead>

            <tbody>
              @for (med of medications; track med.name) {
              <tr>
                <td>{{ med.name }}</td>
                <td>{{ med.dosage }}</td>
                <td>{{ med.frequency }}</td>
                <td>{{ med.appliedArea }}</td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      }
    </div>
  </div>

  <div class="actions actions--sticky">
    @if (booking.status === AppointmentStatus.Pending) {
    <button class="btn btn-primary" (click)="confirmBooking()">
      <i class="bi bi-check-circle me-1"></i>Confirm
    </button>
    }

    @if (booking.patientDocumentGeneratable) {
    @if (!booking.showPrescriptionDocumentOption) {
    <button class="btn btn-primary" (click)="createPrescription()">
      <i class="bi bi-file-earmark-medical me-1"></i>Write Prescription
    </button>
    }
    }

    @if (booking.status === AppointmentStatus.Ongoing && canCompleteBooking) {
    <button class="btn btn-success" (click)="completeBooking()">
      <i class="bi bi-check2-all me-1"></i>Complete
    </button>
    }

    @if (
    booking.status === AppointmentStatus.Confirmed ||
    booking.status === AppointmentStatus.Pending
    ) {
    <button class="btn btn-warning" (click)="noShowBooking()">
      <i class="bi bi-person-slash me-1"></i>No Show
    </button>
    }

    @if (booking.status === AppointmentStatus.Confirmed) {
    <button class="btn btn-primary" (click)="startBooking()">
      <i class="bi bi-play-circle me-1"></i>Start
    </button>
    }
  </div>
</div>
}